import { type FC, useCallback } from 'react'

import { type CasesSort, type CasesSortKey, type CasesSortOrder } from '@/pages/_app/types'
import { SortButton } from '@/pages/_app/ui/table/table-sort-button/table-sort-button'

import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/table/table'

import type { CaseSearchItem } from '@/lib/queries/business/types'
import { SearchCasesSortKey } from '@/lib/queries/business/v4/case/search/types'
import { cn } from '@/lib/utils'

import { RowSelectButton } from './cases-table-cell/cell-content-select-row/row-select-button'
import { CasesTableRow } from './cases-table-row/cases-table-row'

export interface CasesTableProps {
  cases: CaseSearchItem[]
  hideApplicant?: boolean
  selectedCaseIds: number[]
  currentSort: CasesSort
  isEveryRowSelected: boolean
  className?: string
  disabled?: boolean
  defaultSort?: CasesSort
  onToggleCaseRowSelection: (caseId: number) => void
  onToggleAllRowSelection: () => void
  onSortChange: (sort: CasesSort) => Promise<void>
}

export const CasesTable: FC<CasesTableProps> = ({
  className,
  disabled = false,
  cases,
  hideApplicant = false,
  selectedCaseIds,
  defaultSort,
  currentSort,
  isEveryRowSelected,
  onToggleCaseRowSelection,
  onToggleAllRowSelection,
  onSortChange,
}) => {
  const sortOrderChangeHandler = useCallback(
    (key: CasesSortKey) => async (order?: CasesSortOrder) => {
      if (key && order) {
        const newSort = { key, order }
        await onSortChange(newSort)
      } else if (key && !order && !defaultSort) {
        await onSortChange({ key: undefined, order: undefined })
      } else if (!order && defaultSort) {
        await onSortChange(defaultSort)
      }
    },
    [defaultSort, onSortChange]
  )

  return (
    <div className={cn('rounded-md border', className)}>
      {cases.length === 0 && <Empty />}
      {cases.length > 0 && (
        <Table className="text-sm">
          <TableHeader className={cn('h-12')}>
            <TableRow className={cn('text-xs font-medium [&>th]:h-10 [&>th]:py-0', 'hover:bg-transparent')}>
              <TableHead className="w-10 px-0">
                <RowSelectButton disabled={disabled} checked={isEveryRowSelected} onClick={onToggleAllRowSelection} />
              </TableHead>
              {!hideApplicant && <TableHead>Applicant</TableHead>}
              <TableHead>Type</TableHead>
              <TableHead>Tag</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Case Status Message</TableHead>
              <TableHead>
                <SortButton
                  title="Last Updated"
                  sortOrder={currentSort.key === SearchCasesSortKey.UPDATED_AT ? currentSort.order : undefined}
                  onSortOrderChange={sortOrderChangeHandler(SearchCasesSortKey.UPDATED_AT)}
                />
              </TableHead>
              <TableHead>
                <SortButton
                  title="Processing Time"
                  sortOrder={currentSort.key === SearchCasesSortKey.PROCESSING_DAYS ? currentSort.order : undefined}
                  onSortOrderChange={sortOrderChangeHandler(SearchCasesSortKey.PROCESSING_DAYS)}
                />
              </TableHead>
              <TableHead>Case Number</TableHead>
              <TableHead>Assignee</TableHead>
              {/* Empty element for settings button */}
              <TableHead />
            </TableRow>
          </TableHeader>

          <TableBody>
            {cases.map((aCase) => (
              <CasesTableRow
                key={aCase.case_id}
                aCase={aCase}
                hideApplicant={hideApplicant}
                selected={selectedCaseIds.includes(aCase.case_id)}
                onToggleCaseRowSelection={onToggleCaseRowSelection}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}

const Empty = () => {
  return (
    <div className={cn('flex h-[570px] items-center justify-center', 'text-center text-gray-500')}>No cases yet.</div>
  )
}
